// How well a bot plays: one number, five bands (AI_MANAGERS.md §4.1–4.3).
//
// SKILL IS NOT PERSONALITY. The dials say how a bot behaves; this says how
// well it judges what it sees — how far its reading of a projection strays,
// how big a gain it holds out for in a trade it offers, how good an offer has
// to be before it takes one, how it drafts. The goofy bot is not the bad bot:
// Uncle Rico at Ruthless and Uncle Rico at Casual are both Uncle Rico.
//
// ONE NUMBER, 0 TO 100, in five named bands, because a number is not a thing
// anybody picks. Each band's number is the middle of its fifth of the range,
// and every competence is a value at each band's number with a straight line
// between them (`byBand`), so the three levels the cast had before the bands
// — casual, solid and sharp — play exactly as they did.
//
// SKILL IS NEVER ACCESS. A Ruthless bot reads the projections every person
// can see, and reads them well; an Along for the Ride bot reads the same ones
// badly on purpose. Nothing either knows is hidden from anybody.
//
// EVERY BAND CLEARS THE FLOOR (§4.3). A lineup that can score, a pickup for a
// slot nobody can fill and an answer to every offer are not skill; nothing
// here turns them off.

import { SKILL_BANDS } from '@fsp/domain';

/** The bands a person chooses from, the easiest first (§4.2): the domain's, so the wire and the engine name the same five. */
export { SKILL_BANDS };
export type SkillBand = (typeof SKILL_BANDS)[number];

/** Each band's number: the middle of its fifth of the range. */
export const BAND_SKILL: Readonly<Record<SkillBand, number>> = {
  along: 10,
  casual: 30,
  solid: 50,
  sharp: 70,
  ruthless: 90,
};

/**
 * What each band is called and promises, as the chooser says it (§4.2). Only
 * what skill decides: how often a bot looks at its team and whether it works
 * the wire are its dials, whatever its band.
 */
export const BANDS: Readonly<Record<SkillBand, { label: string; promise: string }>> = {
  along: {
    label: 'Along for the ride',
    promise: 'Fields a legal team every week, reads the numbers loosely and starts its own guys.',
  },
  casual: {
    label: 'Casual',
    promise: 'Beatable, and occasionally lucky.',
  },
  solid: { label: 'Solid', promise: 'A normal league-mate. Wins some.' },
  sharp: { label: 'Sharp', promise: 'Reads the numbers closely and holds out for a fair trade.' },
  ruthless: {
    label: 'Ruthless',
    promise: 'Reads the numbers as closely as they go, and says no to a bad offer.',
  },
};

/** The band a number falls in. */
export function bandOf(skill: number): SkillBand {
  const s = Math.min(100, Math.max(0, skill));
  return SKILL_BANDS[Math.min(SKILL_BANDS.length - 1, Math.floor(s / 20))]!;
}

/**
 * A competence at `skill`: each band's value at its own number, a straight
 * line between two bands, and the end bands' values past them.
 */
export function byBand(skill: number, values: Readonly<Record<SkillBand, number>>): number {
  const points = SKILL_BANDS.map((b) => [BAND_SKILL[b], values[b]] as const);
  const s = Math.min(100, Math.max(0, skill));
  // A BAND'S OWN NUMBER IS ITS OWN VALUE, exactly, not the far end of a line
  // worked out in floating point: the cast's levels play as they always did.
  const at = SKILL_BANDS.find((b) => BAND_SKILL[b] === s);
  if (at !== undefined) return values[at];
  if (s <= points[0]![0]) return points[0]![1];
  for (let i = 1; i < points.length; i += 1) {
    const [x1, y1] = points[i]!;
    if (s <= x1) {
      const [x0, y0] = points[i - 1]!;
      return y0 + ((y1 - y0) * (s - x0)) / (x1 - x0);
    }
  }
  return points[points.length - 1]![1];
}
